import { useId, useState, type ReactNode } from 'react';
import { ArrowSquareOut, CaretDown, FileText } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type SourceDocumentProps = {
  /** What the document is ("Original job ad"). */
  title: ReactNode;
  /** Where it comes from, in words ("As published by Esperi Care on career.esperi.fi"). */
  source?: ReactNode;
  /** The document at its source. */
  href?: string;
  /** Label of the source link ("Open the ad"). */
  linkLabel?: ReactNode;
  /** Clip long text to a preview with a "show all" control; the full text stays in the HTML. */
  collapsible?: boolean;
  expandLabel?: ReactNode;
  collapseLabel?: ReactNode;
  id?: string;
  /** A provenance footnote under the text. */
  footer?: ReactNode;
  /** The verbatim text (usually Prose). */
  children: ReactNode;
  className?: string;
};

/**
 * Verbatim text from somewhere else, kept visibly apart from what the product wrote: a framed
 * document with a source bar on top (document icon, title, where it is from, a link to it) and the
 * text below on its own paper. Use it whenever a page shows both a transformed summary and the
 * source it came from, so nobody mistakes one for the other.
 */
export function SourceDocument({
  title,
  source,
  href,
  linkLabel,
  collapsible = false,
  expandLabel = 'Show all',
  collapseLabel = 'Show less',
  id,
  footer,
  children,
  className,
}: SourceDocumentProps) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const clipped = collapsible && !open;
  return (
    <section
      id={id}
      className={cx(
        'scroll-mt-24 overflow-hidden rounded-jc-container border border-jc-border-control bg-jc-surface text-jc-ink',
        className,
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-jc-border bg-jc-elevated px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 text-jc-ink-secondary">
            <Icon icon={FileText} size="md" />
          </span>
          <div className="flex min-w-0 flex-col">
            <h2 className="m-0 type-subheading text-jc-ink">{title}</h2>
            {source ? <p className="m-0 type-caption text-jc-ink-secondary">{source}</p> : null}
          </div>
        </div>
        {href && linkLabel ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex flex-none items-center gap-1 text-sm font-semibold text-jc-ink underline decoration-current/30 underline-offset-2 hover:decoration-current"
          >
            {linkLabel}
            <Icon icon={ArrowSquareOut} size="sm" />
          </a>
        ) : null}
      </header>
      <div className="relative">
        <div
          id={bodyId}
          className={cx('px-4 py-5 md:px-6', clipped && 'max-h-80 overflow-hidden')}
          // Clipped, not removed: the whole text is in the HTML for readers and crawlers.
        >
          {children}
        </div>
        {clipped ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-jc-surface to-transparent"
          />
        ) : null}
      </div>
      {footer ? <p className="m-0 px-4 pb-4 type-caption text-jc-ink-secondary md:px-6">{footer}</p> : null}
      {collapsible ? (
        <div className="border-t border-jc-border px-4 py-3 md:px-6">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 items-center gap-1.5 rounded-full border border-jc-border-control px-4 text-sm font-semibold text-jc-ink transition-colors hover:bg-jc-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
          >
            <Icon icon={CaretDown} size="sm" className={cx('transition-transform', open && 'rotate-180')} />
            {/* Both labels stay mounted and swap by class, so a Google Translate rewritten text
                node is never removed by React on toggle. */}
            <span className={open ? 'hidden' : undefined}>{expandLabel}</span>
            <span className={open ? undefined : 'hidden'}>{collapseLabel}</span>
          </button>
        </div>
      ) : null}
    </section>
  );
}
